<template>
  <section
    id="comments"
    class="space-y-6"
    dir="rtl"
  >
    <div class="flex items-end justify-between gap-3">
      <div>
        <h2 class="text-xl font-bold">
          دیدگاه‌ها
        </h2>
        <p class="mt-1 text-sm text-[var(--ui-text-muted)]">
          {{ commentsEnabled ? 'پرسش خود را با پزشک در میان بگذارید.' : 'دیدگاه برای این مقاله بسته است.' }}
        </p>
      </div>
      <UBadge
        v-if="visibleCount"
        variant="subtle"
        color="neutral"
      >
        {{ toFaDigits(visibleCount) }} دیدگاه
      </UBadge>
    </div>

    <div
      v-if="commentsEnabled && !commentsClosed"
      class="overflow-hidden rounded-2xl border border-[var(--ui-border)] bg-[var(--arad-surface)]"
    >
      <div
        v-if="requireLogin && !loggedIn"
        class="flex flex-col"
      >
        <UTextarea
          v-model="draft.body"
          autoresize
          :rows="3"
          :maxrows="8"
          placeholder="دیدگاه خود را بنویسید…"
          class="w-full"
          :ui="{
            root: 'w-full',
            base: 'w-full rounded-none border-0 bg-transparent px-4 py-3.5 text-[15px] leading-7 shadow-none ring-0 focus:ring-0',
          }"
        />
        <div class="flex flex-col gap-3 border-t border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p class="text-xs leading-5 text-[var(--ui-text-muted)]">
            برای ثبت دیدگاه ابتدا وارد شوید. متن شما ذخیره می‌شود.
          </p>
          <UButton
            class="shrink-0 self-start sm:self-auto"
            icon="i-lucide-log-in"
            @click="goLogin"
          >
            ورود / ثبت‌نام
          </UButton>
        </div>
      </div>
      <form
        v-else
        class="flex flex-col"
        @submit.prevent="submit()"
      >
        <div
          v-if="!loggedIn"
          class="grid gap-3 border-b border-[var(--ui-border)] px-4 pt-4 sm:grid-cols-2"
        >
          <UFormField label="نام نمایشی">
            <UInput
              v-model="draft.authorName"
              placeholder="مثلاً سارا"
            />
          </UFormField>
          <UFormField label="ایمیل (اختیاری)">
            <UInput
              v-model="draft.authorEmail"
              dir="ltr"
              placeholder="name@email.com"
            />
          </UFormField>
        </div>
        <UTextarea
          v-model="draft.body"
          autoresize
          :rows="3"
          :maxrows="10"
          :maxlength="maxLength"
          placeholder="دیدگاه یا پرسش خود را بنویسید…"
          class="w-full"
          :ui="{
            root: 'w-full',
            base: 'w-full rounded-none border-0 bg-transparent px-4 py-3.5 text-[15px] leading-7 shadow-none ring-0 focus:ring-0',
          }"
        />
        <div class="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--ui-border)] bg-[var(--ui-bg-muted)]/40 px-4 py-3">
          <div class="min-w-0 flex-1 space-y-0.5">
            <p class="text-xs leading-5 text-[var(--ui-text-muted)]">
              {{ guestHint }}
            </p>
            <p
              v-if="draft.body.length > 0"
              class="text-[11px] tabular-nums text-[var(--ui-text-dimmed)]"
              dir="ltr"
            >
              {{ toFaDigits(draft.body.length) }} / {{ toFaDigits(maxLength) }}
            </p>
          </div>
          <UButton
            type="submit"
            class="shrink-0"
            icon="i-lucide-send"
            :loading="submitting"
            :disabled="!draft.body.trim()"
          >
            ثبت دیدگاه
          </UButton>
        </div>
      </form>
    </div>

    <AppEmptyState
      v-if="!tree.length"
      icon="i-lucide-message-square"
      title="هنوز دیدگاهی ثبت نشده"
      description="اولین پرسش را شما بگذارید."
    />

    <ul
      v-else
      class="space-y-4"
    >
      <li
        v-for="item in tree"
        :key="item.id"
      >
        <BlogCommentCard
          :comment="item"
          :can-reply="commentsEnabled && !commentsClosed && (!requireLogin || loggedIn)"
          @reply="replyTo"
          @remove="remove"
        />
      </li>
    </ul>

    <UModal v-model:open="replyOpen">
      <template #content>
        <AppFormCard
          title="پاسخ"
          description="پاسخ شما زیر همان دیدگاه نمایش داده می‌شود."
        >
          <UTextarea
            v-model="replyBody"
            autoresize
            :rows="4"
          />
          <template #footer>
            <div class="flex justify-end gap-2">
              <UButton
                variant="ghost"
                @click="replyOpen = false"
              >
                انصراف
              </UButton>
              <UButton
                :loading="submitting"
                @click="submit(replyParentId)"
              >
                ارسال پاسخ
              </UButton>
            </div>
          </template>
        </AppFormCard>
      </template>
    </UModal>
  </section>
</template>

<script setup lang="ts">
import type { BlogCommentPublic } from '~/types/blog'
import { apiErrorMessage } from '~/utils/apiError'
import { toFaDigits } from '~/utils/faDigits'

const props = defineProps<{
  slug: string
  commentsEnabled: boolean
  requireLogin: boolean
  commentsClosed?: boolean
  maxLength?: number
}>()

const route = useRoute()
const draft = reactive({ body: '', authorName: '', authorEmail: '' })
const submitting = ref(false)
const replyOpen = ref(false)
const replyParentId = ref<string | null>(null)
const replyBody = ref('')
const tree = ref<BlogCommentPublic[]>([])

const { data: session } = await useFetch<{ loggedIn: boolean }>('/api/patient/session', {
  default: () => ({ loggedIn: false }),
})
const loggedIn = computed(() => Boolean(session.value?.loggedIn))
const maxLength = computed(() => props.maxLength || 2000)

const guestHint = computed(() => {
  if (loggedIn.value) return 'دیدگاه با نام حساب شما ثبت می‌شود.'
  return 'دیدگاه مهمان پس از تأیید پزشک منتشر می‌شود.'
})

const visibleCount = computed(() => countVisible(tree.value))

function countVisible(items: BlogCommentPublic[]): number {
  return items.reduce((sum, item) => {
    const self = item.status === 'deleted' ? 0 : 1
    return sum + self + countVisible(item.replies || [])
  }, 0)
}

function guestTokens(): string[] {
  if (!import.meta.client) return []
  try {
    return JSON.parse(localStorage.getItem('aradcore:blog-guest-tokens') || '[]') as string[]
  }
  catch {
    return []
  }
}

function rememberGuestToken(token: string) {
  const next = [...new Set([...guestTokens(), token])].slice(-40)
  localStorage.setItem('aradcore:blog-guest-tokens', JSON.stringify(next))
}

async function load() {
  const res = await $fetch<{ items: BlogCommentPublic[] }>(`/api/blog/posts/${encodeURIComponent(props.slug)}/comments`, {
    headers: { 'x-blog-guest-tokens': guestTokens().join(',') },
  })
  tree.value = res.items
}

async function submit(parentId?: string | null) {
  const body = parentId ? replyBody.value : draft.body
  submitting.value = true
  try {
    const res = await $fetch<{ item: { id: string; status: string; guestToken?: string } }>(
      `/api/blog/posts/${encodeURIComponent(props.slug)}/comments`,
      {
        method: 'POST',
        body: {
          body,
          parentId: parentId || undefined,
          authorName: draft.authorName,
          authorEmail: draft.authorEmail,
          guestToken: guestTokens()[0],
        },
      },
    )
    if (res.item.guestToken) rememberGuestToken(res.item.guestToken)
    draft.body = ''
    replyBody.value = ''
    replyOpen.value = false
    sessionStorage.removeItem(draftKey())
    useToast().add({
      title: res.item.status === 'pending' ? 'دیدگاه ثبت شد' : 'دیدگاه منتشر شد',
      description: res.item.status === 'pending' ? 'پس از تأیید پزشک نمایش داده می‌شود.' : undefined,
    })
    await load()
  }
  catch (error) {
    const status = (error as { statusCode?: number })?.statusCode
    if (status === 401) {
      persistDraft()
      await goLogin()
      return
    }
    useToast().add({ title: 'خطا', description: apiErrorMessage(error), color: 'error' })
  }
  finally {
    submitting.value = false
  }
}

function draftKey() {
  return `aradcore:blog-comment-draft:${props.slug}`
}

function persistDraft() {
  sessionStorage.setItem(draftKey(), JSON.stringify({ ...draft }))
}

function restoreDraft() {
  try {
    const raw = sessionStorage.getItem(draftKey())
    if (!raw) return
    Object.assign(draft, JSON.parse(raw))
  }
  catch {
    // ignore
  }
}

function goLogin() {
  persistDraft()
  return navigateTo({
    path: '/patient/login',
    query: { redirect: `${route.path}#comments` },
  })
}

function replyTo(id: string) {
  if (props.requireLogin && !loggedIn.value) {
    goLogin()
    return
  }
  replyParentId.value = id
  replyBody.value = ''
  replyOpen.value = true
}

async function remove(id: string) {
  try {
    await $fetch(`/api/blog/comments/${id}`, {
      method: 'DELETE',
      body: { guestTokens: guestTokens() },
    })
    await load()
  }
  catch (error) {
    useToast().add({ title: 'خطا', description: apiErrorMessage(error), color: 'error' })
  }
}

onMounted(async () => {
  restoreDraft()
  try {
    await load()
  }
  catch {
    tree.value = []
  }
})
</script>
